import {
  CheckCircleTwoTone,
  MinusOutlined,
  PlusOutlined,
  RedoOutlined,
  SaveOutlined,
} from '@ant-design/icons';

import {
  Alert,
  Checkbox,
  Input,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Confetti from 'react-confetti';
import { useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';

// Extend the Window interface to include triggerConfetti
declare global {
  interface Window {
    triggerConfetti: () => void;
  }
}

import { ClientNavigation } from '../../components/client/client-navigation';
import { HWButton } from '../../components/shared/StyledButton';
import { ClientConfig, OnboardingFlow } from '../../types';
import { UserContext } from '../../UserContext';

interface UpdateData {
  greeting_message?: string;
  phone_override?: string;
  is_isa?: boolean;
}

interface OnboardingStatus {
  id: string;
  source_id: string;
  agent_id: string;
  is_complete: boolean;
  stage: string;
  first_name: string;
  last_name: string;
  has_inbound_message: boolean;
  outbound_message_count?: number;
  call_time?: string;
}

interface CombinedOnboardingData extends OnboardingFlow {
  status?: OnboardingStatus;
  greeting_sent?: boolean; // Add indicator for greeting sent
}

export const OnboardingNewPage = ({ apiUrl }: { apiUrl: string }) => {
  const user = useContext(UserContext);
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);

  const [onboardingData, setOnboardingData] = useState<
    Record<string, CombinedOnboardingData>
  >({});
  const [clientInfo, setClientInfo] = useState<ClientConfig | null>(null);
  const [updatedData, setUpdatedData] = useState<Record<string, UpdateData>>(
    {},
  );
  const [loadingSendAll, setLoadingSendAll] = useState(false);
  const [loadingSendAllZeroOutbound, setLoadingSendAllZeroOutbound] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [searchTerm, setSearchTerm] = useState('');
  const [hideCompleted, setHideCompleted] = useState(false);
  const [showCompleteFlows, setShowCompleteFlows] = useState(false);
  const [bioCounts, setBioCounts] = useState<Record<string, number>>({});
  const { clientId } = useParams<{ clientId: string }>();

  // Fetch client info
  const fetchClientInfo = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/configs/${clientId}/info`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch client info: ${response.statusText}`);
      }
      const clientData: ClientConfig = await response.json();
      setClientInfo(clientData);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch client information');
    }
  };

  // Fetch onboarding flow definitions
  const fetchFlowData = async (): Promise<Record<string, OnboardingFlow>> => {
    try {
      setLoading(true);
      const flowResponse = await fetch(
        `${apiUrl}/api/onboarding/flow/${clientId}?get_complete=${showCompleteFlows}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      if (!flowResponse.ok) {
        throw new Error(
          `Failed to fetch flow data: ${flowResponse.statusText}`,
        );
      }
      const flowArray: OnboardingFlow[] = await flowResponse.json();
      return flowArray.reduce(
        (acc, flow) => {
          acc[flow.id] = flow;
          return acc;
        },
        {} as Record<string, OnboardingFlow>,
      );
    } catch (err) {
      console.error(err);
      setError('Failed to fetch onboarding flow data');
      return {};
    } finally {
      setLoading(false);
    }
  };

  // Fetch onboarding status per agent
  const fetchStatusData = async (): Promise<
    Record<string, OnboardingStatus>
  > => {
    try {
      const statusResponse = await fetch(
        `${apiUrl}/api/onboarding/status/${clientId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      if (!statusResponse.ok) {
        throw new Error(
          `Failed to fetch status data: ${statusResponse.statusText}`,
        );
      }
      const statusArray: OnboardingStatus[] = await statusResponse.json();
      return statusArray.reduce(
        (acc, status) => {
          acc[status.agent_id] = status;
          return acc;
        },
        {} as Record<string, OnboardingStatus>,
      );
    } catch (err) {
      console.error(err);
      setError('Failed to fetch status data');
      return {};
    }
  };

  const fetchBioCounts = async (): Promise<Record<string, number>> => {
    try {
      const bioCountsResponse = await fetch(
        `${apiUrl}/api/onboarding/bio-counts/${clientId}?get_complete=${showCompleteFlows}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      if (!bioCountsResponse.ok) {
        throw new Error(
          `Failed to fetch bio counts: ${bioCountsResponse.statusText}`,
        );
      }
      const bioCountsData: Record<string, number> =
        await bioCountsResponse.json();
      return bioCountsData;
    } catch (err) {
      console.error(err);
      setError('Failed to fetch bio counts');
      return {};
    }
  };

  // Combine flows + statuses into one object
  const updateCombinedData = (
    flows: Record<string, OnboardingFlow>,
    statuses: Record<string, OnboardingStatus>,
  ) => {
    const combined = Object.entries(flows).reduce(
      (acc, [id, flow]) => {
        acc[id] = { ...flow, status: statuses[flow.agent_id] };
        return acc;
      },
      {} as Record<string, CombinedOnboardingData>,
    );
    setOnboardingData(combined);
  };

  // Initial data load
  useEffect(() => {
    const initialize = async () => {
      await fetchClientInfo();
      const flows = await fetchFlowData();
      const statuses = await fetchStatusData();
      const bioCountsData = await fetchBioCounts();
      updateCombinedData(flows, statuses);
      setBioCounts(bioCountsData);
    };
    void initialize();
  }, [clientId, showCompleteFlows]);

  useEffect(() => {
    window.triggerConfetti = () => {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 10000); // confetti for 3 seconds
    };
  }, []);

  // Poll status every minute
  useEffect(() => {
    const interval = setInterval(() => {
      void (async () => {
        const statuses = await fetchStatusData();
        updateCombinedData(onboardingData, statuses);
      })();
    }, 60000);
    return () => clearInterval(interval);
  }, [onboardingData]);

  // Local edits
  const handleUpdateData = (
    key: keyof UpdateData,
    id: string,
    value: unknown,
  ) => {
    setUpdatedData(prev => ({
      ...prev,
      [id]: { ...prev[id], [key]: value },
    }));
  };

  // Save phone override
  const savePhoneOverride = async (flow_id: string) => {
    const override = updatedData[flow_id]?.phone_override;
    const flow = onboardingData[flow_id];
    if (!flow || !override) {
      setError('Phone override cannot be empty');
      return;
    }
    try {
      const res = await fetch(`${apiUrl}/api/agent/${flow.agent_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({ phone_override: override }),
      });
      if (!res.ok) {
        throw new Error(res.statusText);
      }
      const json = await res.json();
      const copy = { ...onboardingData };
      copy[flow_id].agent.phone_override = json.phone_override;
      setOnboardingData(copy);
      handleUpdateData('phone_override', flow_id, '');
    } catch (err) {
      console.error(err);
      setError('Failed to save phone override');
    }
  };

  // Save greeting message
  const saveGreetingMessage = async (flow_id: string) => {
    const newMsg = updatedData[flow_id]?.greeting_message;
    if (!newMsg) {
      setError('Greeting Message cannot be empty');
      return;
    }
    try {
      const res = await fetch(`${apiUrl}/api/onboarding/flow/${flow_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({ greeting_message: newMsg }),
      });
      if (!res.ok) {
        throw new Error(res.statusText);
      }
      const copy = { ...onboardingData };
      copy[flow_id].greeting_message = newMsg;
      setOnboardingData(copy);
      handleUpdateData('greeting_message', flow_id, '');
    } catch (err) {
      console.error(err);
      setError('Failed to save greeting message');
    }
  };

  // Regenerate greeting
  const redoGreetingMessage = async (flow_id: string) => {
    const flow = onboardingData[flow_id];
    try {
      const res = await fetch(
        `${apiUrl}/api/onboarding/flow/${flow.agent_id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      if (!res.ok) {
        throw new Error(res.statusText);
      }
      const json = await res.json();
      const copy = { ...onboardingData };
      copy[flow_id] = { ...copy[flow_id], ...json };
      setOnboardingData(copy);
    } catch (err) {
      console.error(err);
      setError('Failed to regenerate greeting');
    }
  };

  // Low-level send greeting
  const _sendGreetingMessage = async (flow_id: string) => {
    const flow = onboardingData[flow_id];
    const res = await fetch(
      `${apiUrl}/api/onboarding/send-greeting/${flow.agent_id}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
      },
    );
    if (!res.ok) {
      let message = res.statusText;
      try {
        const data = await res.json();
        message =
          (data && (data.detail || data.error || data.message)) || message;
      } catch {
        try {
          const txt = await res.text();
          if (txt) {
            message = txt;
          }
        } catch {
          // ignore
        }
      }
      throw new Error(`Failed to send greeting: ${message}`);
    }
    const json = await res.json();
    const copy = { ...onboardingData };
    copy[flow_id].greeting_sent_at = json.onboarding_flow.greeting_sent_at;
    copy[flow_id].greeting_sent = true; // Add indicator for greeting sent
    setOnboardingData(copy);
  };

  const publishBioForAgent = async (agent_id: string) => {
    try {
      const res = await fetch(`${apiUrl}/api/onboarding/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({
          client_id: clientId,
          agent_ids: [agent_id],
        }),
      });
      if (!res.ok) {
        console.error(
          `Failed to publish bio for agent ${agent_id}:`,
          res.statusText,
        );
      } else {
        console.log(`Successfully published bio for agent ${agent_id}`);
      }
    } catch (err) {
      console.error(`Error publishing bio for agent ${agent_id}:`, err);
    }
  };

  // Public send greeting
  const sendGreetingMessage = async (flow_id: string, all = false) => {
    try {
      await _sendGreetingMessage(flow_id);

      const flow = onboardingData[flow_id];
      if (flow?.agent?.id) {
        void publishBioForAgent(flow.agent.id);
      }

      if (!all) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
    } catch (err: any) {
      const msg = err?.message
        ? String(err.message)
        : 'Failed to send greeting';
      if (all) {
        throw new Error(msg);
      } else {
        setError(msg);
      }
    }
  };

  // Send calendar invite
  const sendCalendarInvite = async (record: CombinedOnboardingData) => {
    try {
      const res = await fetch(`${apiUrl}/api/calendar/send-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({
          agent_id: record.agent_id,
          client_id: clientId,
        }),
      });
      if (!res.ok) {
        throw new Error(res.statusText);
      }
      setError(`Calendar invite sent to ${record.agent.email}`);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    } catch (err) {
      console.error(err);
      setError('Error sending calendar invite');
    }
  };

  // Batch send all
  const sendAll = async () => {
    try {
      const usable = getUsableRows();
      if (!usable.length) {
        setError('No rows with valid phone numbers to send messages to');
        return;
      }
      setLoadingSendAll(true);
      const limit = 10;
      const flowIds = usable.map(r => r.id);
      let successCount = 0;
      const exceptions: unknown[] = [];

      for (let i = 0; i < flowIds.length; i += limit) {
        const chunk = flowIds.slice(i, i + limit);
        const results = await Promise.allSettled(
          chunk.map(id => sendGreetingMessage(id, true)),
        );
        results.forEach(r => {
          if (r.status === 'fulfilled') {
            successCount++;
          } else {
            exceptions.push(r.reason);
          }
        });
      }

      if (exceptions.length) {
        const msgs = exceptions.map((e: any) =>
          e?.message ? String(e.message) : String(e),
        );
        setError(`Some greetings failed:\n${msgs.join('\n')}`);
      }
      if (successCount > 0) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 10000);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to send all greetings');
    } finally {
      setLoadingSendAll(false);
    }
  };

  // Batch send only to agents with zero outbound messages
  const sendAllZeroOutbound = async () => {
    try {
      const usable = getUsableZeroOutboundRows();
      if (!usable.length) {
        setError('No agents with 0 outbound messages and valid phone numbers');
        return;
      }
      setLoadingSendAllZeroOutbound(true);
      const limit = 10;
      const flowIds = usable.map(r => r.id);
      let successCount = 0;
      const exceptions: unknown[] = [];

      for (let i = 0; i < flowIds.length; i += limit) {
        const chunk = flowIds.slice(i, i + limit);
        const results = await Promise.allSettled(
          chunk.map(id => sendGreetingMessage(id, true)),
        );
        results.forEach(r => {
          if (r.status === 'fulfilled') {
            successCount++;
          } else {
            exceptions.push(r.reason);
          }
        });
      }

      if (exceptions.length) {
        const msgs = exceptions.map((e: any) =>
          e?.message ? String(e.message) : String(e),
        );
        setError(`Some greetings failed:\n${msgs.join('\n')}`);
      }
      if (successCount > 0) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 10000);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to send greetings to 0-outbound agents');
    } finally {
      setLoadingSendAllZeroOutbound(false);
    }
  };

  // Mark failed
  const markFailed = async (flow_id: string) => {
    try {
      const res = await fetch(
        `${apiUrl}/api/onboarding/flow/${flow_id}/mark-failed`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      if (!res.ok) {
        throw new Error(res.statusText);
      }
      const copy = { ...onboardingData };
      copy[flow_id].stage = 'failed';
      copy[flow_id].status = copy[flow_id].status && {
        ...copy[flow_id].status!,
        stage: 'failed',
        is_complete: true,
      };
      setOnboardingData(copy);
    } catch (err) {
      console.error(err);
      setError('Failed to mark as failed');
    }
  };

  // Toggle ISA
  const saveIsIsa = async (flow_id: string) => {
    const flow = onboardingData[flow_id];
    const newIsa = !flow.agent.is_isa;
    try {
      const res = await fetch(`${apiUrl}/api/agent/${flow.agent_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
        body: JSON.stringify({ is_isa: newIsa }),
      });
      if (!res.ok) {
        throw new Error(res.statusText);
      }
      const copy = { ...onboardingData };
      copy[flow_id].agent.is_isa = newIsa;
      setOnboardingData(copy);
    } catch (err) {
      console.error(err);
      setError('Failed to save ISA status');
    }
  };

  // Helpers for phone/status
  const isFlowDisabled = (record: CombinedOnboardingData) => {
    const s = record.status?.stage?.toLowerCase();
    return s === 'complete' || s === 'failed';
  };

  const isValidPhone = useCallback((phone: string) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return /^[2-9]\d{9}$/.test(digits);
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return /^1[2-9]\d{9}$/.test(digits);
    }
    return false;
  }, []);

  const duplicatePhones = useMemo(() => {
    const map = new Map<string, string[]>();
    Object.values(onboardingData).forEach(d => {
      const override =
        updatedData[d.id]?.phone_override || d.agent.phone_override;
      const raw = override || d.agent.phone || '';
      const norm = raw.replace(/\D/g, '');
      if (norm) {
        map.set(norm, [
          ...(map.get(norm) || []),
          `${d.agent.first_name} ${d.agent.last_name}`,
        ]);
      }
    });
    return Array.from(map.entries())
      .filter(([, arr]) => arr.length > 1)
      .map(([phone, agents]) => ({ phone, agents }));
  }, [onboardingData, updatedData]);

  const isDuplicatePhone = useCallback(
    (phone: string, record?: CombinedOnboardingData) => {
      const norm = phone.replace(/\D/g, '');
      if (record) {
        const eff = (
          updatedData[record.id]?.phone_override ||
          record.agent.phone_override ||
          record.agent.phone ||
          ''
        ).replace(/\D/g, '');
        if (norm === eff) {
          const dup = duplicatePhones.find(d => d.phone === norm);
          return dup ? dup.agents.length > 1 : false;
        }
      }
      return duplicatePhones.some(d => d.phone === norm);
    },
    [duplicatePhones, updatedData],
  );

  const getEffectivePhone = useCallback(
    (record: CombinedOnboardingData) =>
      updatedData[record.id]?.phone_override ||
      record.agent.phone_override ||
      record.agent.phone ||
      '',
    [updatedData],
  );

  const isPhoneUsable = useCallback(
    (record: CombinedOnboardingData) => {
      const phone = getEffectivePhone(record);
      if (!phone) {
        return false;
      }
      return isValidPhone(phone) && !isDuplicatePhone(phone, record);
    },
    [getEffectivePhone, isValidPhone, isDuplicatePhone],
  );

  const getActiveRows = useCallback(
    () => Object.values(onboardingData).filter(d => !isFlowDisabled(d)),
    [onboardingData],
  );

  const getUsableRows = useCallback(
    () => getActiveRows().filter(r => isPhoneUsable(r)),
    [getActiveRows, isPhoneUsable],
  );

  const hasUsableRows = useMemo(
    () => getUsableRows().length > 0,
    [getUsableRows],
  );

  const getUsableZeroOutboundRows = useCallback(
    () =>
      getUsableRows().filter(
        r => (r.status?.outbound_message_count ?? 0) === 0,
      ),
    [getUsableRows],
  );

  const hasUsableZeroOutboundRows = useMemo(
    () => getUsableZeroOutboundRows().length > 0,
    [getUsableZeroOutboundRows],
  );

  const filteredData = useMemo(() => {
    return Object.values(onboardingData)
      .filter(d => {
        const name = `${d.agent.first_name} ${d.agent.last_name}`.toLowerCase();
        if (!name.includes(searchTerm.toLowerCase())) {
          return false;
        }
        if (hideCompleted && isFlowDisabled(d)) {
          return false;
        }
        return true;
      })
      .sort((a, b) =>
        `${a.agent.first_name} ${a.agent.last_name}`.localeCompare(
          `${b.agent.first_name} ${b.agent.last_name}`,
        ),
      );
  }, [onboardingData, searchTerm, hideCompleted]);
  const columns: ColumnsType<CombinedOnboardingData> = [
    {
      title: 'Name',
      key: 'name',
      className: 'actionable-column',
      render: (_, r) => (
        <Space direction="vertical" size="small">
          <div>
            <span>{`${r.agent.first_name} ${r.agent.last_name}`}</span>
            <span
              style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}
            >
              {r.agent.email}
            </span>
          </div>
          {r.greeting_sent && <Tag color="green">Greeting Sent</Tag>}
          <Space size="small">
            <HWButton
              disabled={isFlowDisabled(r) || !isPhoneUsable(r)}
              onClick={() => void sendGreetingMessage(r.id)}
              size="small"
            >
              Send Greeting
            </HWButton>
            <HWButton
              disabled={isFlowDisabled(r) || !r.status?.call_time}
              onClick={() => void sendCalendarInvite(r)}
              size="small"
            >
              Calendar
            </HWButton>
            <HWButton
              disabled={isFlowDisabled(r)}
              onClick={() => void markFailed(r.id)}
              size="small"
            >
              Mark Failed
            </HWButton>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Phone',
      key: 'phone',
      className: 'actionable-column',
      render: (_, r) => {
        const p = getEffectivePhone(r);
        const dup = p && isDuplicatePhone(p, r);
        const inv = p && !isValidPhone(p);
        return (
          <span
            style={
              dup || inv
                ? {
                    color: '#ff4d4f',
                    textDecoration: inv ? 'line-through' : undefined,
                  }
                : undefined
            }
          >
            {p}
          </span>
        );
      },
    },
    {
      title: 'Phone Override',
      key: 'phone_override',
      className: 'actionable-column',
      render: (_, r) => {
        const o =
          updatedData[r.id]?.phone_override || r.agent.phone_override || '';
        const dup = o && isDuplicatePhone(o, r);
        const inv = o && !isValidPhone(o);
        return (
          <Space>
            <Input
              disabled={isFlowDisabled(r)}
              onChange={e =>
                handleUpdateData('phone_override', r.id, e.target.value)
              }
              placeholder="Phone Override"
              status={dup || inv ? 'error' : undefined}
              style={{ width: 150 }}
              value={o}
            />
            <HWButton
              disabled={!o || dup || inv || isFlowDisabled(r)}
              icon={<SaveOutlined />}
              onClick={() => void savePhoneOverride(r.id)}
            />
            {inv && <span style={{ color: '#ff4d4f' }}>Invalid</span>}
          </Space>
        );
      },
    },
    {
      title: 'ISA',
      key: 'is_isa',
      className: 'actionable-column',
      render: (_, r) => (
        <Checkbox
          checked={r.agent.is_isa}
          disabled={isFlowDisabled(r)}
          onChange={() => void saveIsIsa(r.id)}
        />
      ),
    },
    {
      title: 'Onboarding Stage',
      key: 'stage',
      className: 'status-column',
      render: (_, r) => <span>{r.status?.stage || 'N/A'}</span>,
    },

    {
      title: 'Outbound Messages',
      key: 'outbound_messages',
      className: 'status-column',
      render: (_, r) => <span>{r.status?.outbound_message_count || 0}</span>,
    },
    {
      title: 'Agent Texted',
      key: 'agent_texted',
      className: 'status-column',
      render: (_, r) =>
        r.status?.has_inbound_message ? (
          <CheckCircleTwoTone twoToneColor="#52c41a" />
        ) : null,
    },
    {
      title: 'Calendar Setup',
      key: 'calendar_setup',
      className: 'status-column',
      render: (_, r) =>
        r.calendar_sync_confirmed_at ? (
          <CheckCircleTwoTone twoToneColor="#52c41a" />
        ) : null,
    },
    {
      title: 'Backgrounds to Publish',
      key: 'bio_count',
      className: 'status-column',
      render: (_, r) => {
        const count = bioCounts[r.agent_id] || 0;
        return count > 0 ? (
          <Tag color="purple">{count}</Tag>
        ) : (
          <span style={{ color: '#999' }}>0</span>
        );
      },
    },
    {
      title: 'Agent Page',
      key: 'actions',
      className: 'status-column',
      render: (_, r) => (
        <a
          href={`/agentSms/${clientId}?agentId=${r.agent_id}`}
          rel="noopener noreferrer"
          style={{ color: '#1890ff', textDecoration: 'none' }}
          target="_blank"
        >
          Agent Page
        </a>
      ),
    },
  ];

  return (
    <div style={{ padding: 20, width: '100%', maxWidth: '100%' }}>
      <style>
        {`
          .actionable-column {
            background-color: #f8f9ff !important;
          }
          .status-column {
            background-color: #fafafa !important;
          }
          .ant-table-thead > tr > th.actionable-column {
            background-color: #e6f7ff !important;
          }
          .ant-table-thead > tr > th.status-column {
            background-color: #f5f5f5 !important;
          }
        `}
      </style>
      {showConfetti && <Confetti height={height} width={width} />}

      {clientInfo && (
        <>
          <ClientNavigation
            activeTab="onboarding"
            clientId={clientInfo.id}
            clientName={clientInfo.friendly_name}
            crmLink={clientInfo.referral_url}
            crmType={clientInfo.crm_type}
            isSingleAgent={clientInfo.is_single_agent}
            numericClientId={parseInt(clientId ?? '0', 10)}
          />
          <Typography.Title
            level={5}
            style={{ margin: '0 0 20px', color: '#666' }}
          >
            Agent Onboarding
          </Typography.Title>
        </>
      )}

      {error && (
        <Alert
          closable
          message={
            <Typography.Text
              copyable={{ text: error }}
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {error}
            </Typography.Text>
          }
          onClose={() => setError(undefined)}
          style={{ marginBottom: 16 }}
          type="error"
        />
      )}

      {duplicatePhones.length > 0 && (
        <Alert
          banner
          description={
            <>
              {duplicatePhones.map(d => (
                <div key={d.phone}>
                  Phone {d.phone} used by: {d.agents.join(', ')}
                </div>
              ))}
            </>
          }
          message="Duplicate Phone Numbers Detected"
          style={{ marginBottom: 16 }}
          type="warning"
        />
      )}

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by name"
          style={{ width: 300 }}
          value={searchTerm}
        />
        <Checkbox
          checked={hideCompleted}
          onChange={e => setHideCompleted(e.target.checked)}
        >
          Show Agents to Onboard
        </Checkbox>
        <Checkbox
          checked={showCompleteFlows}
          onChange={e => setShowCompleteFlows(e.target.checked)}
        >
          Include Completed Onboardings
        </Checkbox>
        <Tooltip title="Send the onboarding greeting message to all visible agents">
          <HWButton
            disabled={!hasUsableRows}
            loading={loadingSendAll}
            onClick={() => void sendAll()}
            type="primary"
          >
            Send All Greetings
          </HWButton>
        </Tooltip>
        <Tooltip title="Send greetings only to agents with 0 outbound messages">
          <HWButton
            disabled={!hasUsableZeroOutboundRows}
            loading={loadingSendAllZeroOutbound}
            onClick={() => void sendAllZeroOutbound()}
            type="default"
          >
            Greet 0-Outbound Only
          </HWButton>
        </Tooltip>
      </Space>

      <Table
        columns={columns}
        dataSource={filteredData}
        expandable={{
          expandedRowRender: record => (
            <div style={{ padding: 20 }}>
              <div style={{ flex: 1 }}>
                <h4>Greeting Message</h4>
                <Space>
                  <Input.TextArea
                    disabled={isFlowDisabled(record)}
                    onChange={e =>
                      handleUpdateData(
                        'greeting_message',
                        record.id,
                        e.target.value,
                      )
                    }
                    rows={5}
                    style={{ width: '100%', minWidth: 300 }}
                    value={
                      updatedData[record.id]?.greeting_message ||
                      record.greeting_message ||
                      ''
                    }
                  />
                  <Space direction="vertical">
                    <Tooltip title="Regenerate greeting">
                      <HWButton
                        disabled={isFlowDisabled(record)}
                        icon={<RedoOutlined />}
                        onClick={() => void redoGreetingMessage(record.id)}
                      />
                    </Tooltip>
                    <HWButton
                      disabled={
                        !updatedData[record.id]?.greeting_message ||
                        isFlowDisabled(record)
                      }
                      icon={<SaveOutlined />}
                      onClick={() => void saveGreetingMessage(record.id)}
                    />
                  </Space>
                </Space>
              </div>
            </div>
          ),
          expandIcon: ({ expanded, onExpand, record }) =>
            record.greeting_message ? (
              <HWButton
                icon={expanded ? <MinusOutlined /> : <PlusOutlined />}
                onClick={e => onExpand(record, e)}
                type="text"
              />
            ) : null,
        }}
        loading={loading}
        pagination={false}
        rowKey="id"
        scroll={{ x: '100%' }}
      />
    </div>
  );
};
